import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import AIModelService from "../../../service/admin/aiModelService";
import WebButton from "../../../component/library/webButton";
import { FaAws, FaCheckCircle, FaTimesCircle, FaSync, FaInfoCircle, FaSearch } from "react-icons/fa";
import "./style.css";

export default function ModelForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const loader = useLoader();
  const message = useMessage();
  const isEdit = id && id !== "new";
  const modelSelectorRef = useRef(null);

  const [awsModels, setAwsModels] = useState([]);
  const [awsStatus, setAwsStatus] = useState({ configured: false, loading: true });
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelSearch, setModelSearch] = useState("");
  const [modelFilter, setModelFilter] = useState("amazon"); // Default to Amazon only
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    provider: "bedrock",
    modelId: "",
    displayName: "",
    description: "",
    maxTokens: 2048,
    temperature: 0.7,
    isActive: true,
    isDefault: false,
    capabilities: ["text-generation"],
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modelSelectorRef.current && !modelSelectorRef.current.contains(e.target)) {
        setShowModelDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    fetchAwsStatus();
    if (isEdit) fetchModel();
  }, [id]);

  useEffect(() => {
    if (formData.provider === "bedrock" && awsStatus.configured) {
      fetchAwsModels();
    }
  }, [formData.provider, awsStatus.configured]);

  const fetchAwsStatus = async () => {
    try {
      const res = await AIModelService.getBedrockStatus();
      if (res?.status?.code === 200) {
        setAwsStatus({ ...res.data, loading: false });
      } else {
        setAwsStatus({ configured: false, loading: false });
      }
    } catch (err) {
      setAwsStatus({ configured: false, loading: false, error: err.message });
    }
  };

  const fetchAwsModels = async () => {
    setLoadingModels(true);
    try {
      const res = await AIModelService.getBedrockModels();
      if (res?.status?.code === 200 && res.data?.models) {
        setAwsModels(res.data.models);
      } else {
        message.showToast(res?.status?.message || "Failed to fetch AWS models", ToastTypes.WARNING);
        setAwsModels([]);
      }
    } catch (err) {
      message.showToast("Failed to fetch AWS Bedrock models", ToastTypes.ERROR);
      setAwsModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  const fetchModel = async () => {
    loader.showLoading(true);
    try {
      const res = await AIModelService.getModelById(id);
      if (res?.status?.code === 200 && res.data?.model) {
        setFormData(res.data.model);
      } else {
        message.showToast("Model not found", ToastTypes.ERROR);
        navigate("/admin/ai-management");
      }
    } catch (err) {
      message.showToast("Failed to load model", ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAwsModelSelect = (modelId) => {
    const selected = awsModels.find((m) => m.modelId === modelId);
    if (selected) {
      setFormData((prev) => ({
        ...prev,
        modelId: selected.modelId,
        displayName: prev.displayName || selected.modelName || selected.providerName + " " + selected.modelId.split(".")[1],
        name: prev.name || selected.modelId.replace(/[.:]/g, "-").toLowerCase(),
        description: `${selected.providerName} model - ${selected.modelId}`,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.provider || !formData.modelId || !formData.displayName) {
      message.showToast("Please fill all required fields", ToastTypes.WARNING);
      return;
    }

    loader.showLoading(true);
    try {
      let res;
      if (isEdit) {
        res = await AIModelService.updateModel(id, formData);
      } else {
        res = await AIModelService.createModel(formData);
      }

      if (res?.status?.code === 200) {
        message.showToast(isEdit ? "Model updated" : "Model created", ToastTypes.SUCCESS);
        navigate("/admin/ai-management");
      } else {
        message.showToast(res?.error?.message || "Operation failed", ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast("Operation failed", ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
    }
  };

  // Group AWS models by provider
  const groupedModels = awsModels.reduce((acc, model) => {
    const provider = model.providerName || "Other";
    if (!acc[provider]) acc[provider] = [];
    acc[provider].push(model);
    return acc;
  }, {});

  // Filter and search models
  const filteredModels = useMemo(() => {
    let models = awsModels;
    
    // Filter by provider
    if (modelFilter === "amazon") {
      models = models.filter(m => m.providerName?.toLowerCase() === "amazon");
    } else if (modelFilter !== "all") {
      models = models.filter(m => m.providerName?.toLowerCase() === modelFilter.toLowerCase());
    }
    
    // Search filter
    if (modelSearch.trim()) {
      const search = modelSearch.toLowerCase();
      models = models.filter(m => 
        m.modelId?.toLowerCase().includes(search) ||
        m.modelName?.toLowerCase().includes(search) ||
        m.providerName?.toLowerCase().includes(search)
      );
    }
    
    return models;
  }, [awsModels, modelFilter, modelSearch]);

  // Group filtered models by provider
  const filteredGroupedModels = filteredModels.reduce((acc, model) => {
    const provider = model.providerName || "Other";
    if (!acc[provider]) acc[provider] = [];
    acc[provider].push(model);
    return acc;
  }, {});

  // Get unique providers for filter dropdown
  const availableProviders = useMemo(() => {
    const providers = [...new Set(awsModels.map(m => m.providerName).filter(Boolean))];
    return providers.sort();
  }, [awsModels]);

  // Get selected model display name
  const selectedModelDisplay = useMemo(() => {
    if (!formData.modelId) return "";
    const model = awsModels.find(m => m.modelId === formData.modelId);
    return model ? (model.modelName || model.modelId) : formData.modelId;
  }, [formData.modelId, awsModels]);

  return (
    <div className="aim-model-form-container">
      <div className="aim-form-header">
        <button className="aim-back-btn" onClick={() => navigate("/admin/ai-management")}>← Back</button>
        <h1>{isEdit ? "Edit AI Model" : "Add AI Model"}</h1>
      </div>

      {/* AWS Bedrock Status Banner */}
      {formData.provider === "bedrock" && (
        <div className={`aws-status-banner ${awsStatus.configured ? "configured" : "not-configured"}`}>
          <FaAws className="aws-icon" />
          <div className="aws-status-info">
            <span className="aws-status-label">
              {awsStatus.loading ? "Checking AWS Bedrock..." : 
               awsStatus.configured ? "AWS Bedrock Connected" : "AWS Bedrock Not Configured"}
            </span>
            {awsStatus.configured && <span className="aws-region">Region: {awsStatus.region}</span>}
          </div>
          {awsStatus.configured ? <FaCheckCircle className="status-icon success" /> : <FaTimesCircle className="status-icon error" />}
          {!awsStatus.configured && (
            <a href="#aws-setup" className="setup-link" onClick={(e) => { e.preventDefault(); document.getElementById('aws-setup-guide')?.scrollIntoView({ behavior: 'smooth' }); }}>
              Setup Guide
            </a>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="aim-model-form">
        <div className="aim-form-section">
          <h3>Basic Information</h3>

          <div className="aim-form-row">
            <div className="aim-form-group">
              <label>Provider *</label>
              <select value={formData.provider} onChange={(e) => handleChange("provider", e.target.value)}>
                <option value="bedrock">Amazon Bedrock</option>
                <option value="openai">OpenAI</option>
                <option value="deepseek">DeepSeek</option>
                <option value="gemini">Google Gemini</option>
              </select>
            </div>

            {formData.provider === "bedrock" && awsStatus.configured && (
              <div className="aim-form-group">
                <label>
                  Select AWS Model 
                  <button type="button" className="refresh-models-btn" onClick={fetchAwsModels} disabled={loadingModels}>
                    <FaSync className={loadingModels ? "spinning" : ""} />
                  </button>
                </label>
                
                {/* Searchable Model Dropdown */}
                <div className="aim-model-selector" ref={modelSelectorRef}>
                  <div 
                    className="aim-model-selector-input"
                    onClick={() => setShowModelDropdown(!showModelDropdown)}
                  >
                    <span className={selectedModelDisplay ? "" : "aim-placeholder"}>
                      {selectedModelDisplay || (loadingModels ? "Loading models..." : `Select from ${awsModels.length} available models`)}
                    </span>
                    <span className="aim-dropdown-arrow">▼</span>
                  </div>
                  
                  {showModelDropdown && (
                    <div className="aim-model-dropdown">
                      {/* Search and Filter Row */}
                      <div className="aim-model-search-row">
                        <div className="aim-model-search-box">
                          <FaSearch className="aim-model-search-icon" />
                          <input
                            type="text"
                            placeholder="Search models..."
                            value={modelSearch}
                            onChange={(e) => setModelSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                        </div>
                        <select 
                          value={modelFilter} 
                          onChange={(e) => setModelFilter(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="aim-model-filter"
                        >
                          <option value="amazon">Amazon Only</option>
                          <option value="all">All Providers</option>
                        </select>
                      </div>
                      
                      {/* Model List */}
                      <div className="aim-model-list">
                        {filteredModels.length === 0 ? (
                          <div className="aim-model-no-results">No models found</div>
                        ) : (
                          Object.entries(filteredGroupedModels).map(([provider, models]) => (
                            <div key={provider} className="aim-model-group">
                              <div className="aim-model-group-header">{provider}</div>
                              {models.map((m) => (
                                <div
                                  key={m.modelId}
                                  className={`aim-model-option ${formData.modelId === m.modelId ? "selected" : ""}`}
                                  onClick={() => {
                                    handleAwsModelSelect(m.modelId);
                                    setShowModelDropdown(false);
                                    setModelSearch("");
                                  }}
                                >
                                  <span className="aim-model-name">{m.modelName || m.modelId}</span>
                                  {m.isAvailable && <span className="aim-model-check">✓</span>}
                                </div>
                              ))}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <small>{filteredModels.length} of {awsModels.length} models shown (filtered by {modelFilter === "all" ? "all providers" : modelFilter})</small>
              </div>
            )}
          </div>

          <div className="aim-form-row">
            <div className="aim-form-group">
              <label>Internal Name *</label>
              <input type="text" value={formData.name} onChange={(e) => handleChange("name", e.target.value)} placeholder="e.g., nova-pro-text" />
              <small>Used internally for identification</small>
            </div>

            <div className="aim-form-group">
              <label>Display Name *</label>
              <input type="text" value={formData.displayName} onChange={(e) => handleChange("displayName", e.target.value)} placeholder="e.g., Amazon Nova Pro" />
              <small>Shown in UI dropdowns</small>
            </div>
          </div>

          <div className="aim-form-group">
            <label>Model ID *</label>
            <input type="text" value={formData.modelId} onChange={(e) => handleChange("modelId", e.target.value)} placeholder="e.g., amazon.nova-pro-v1:0" />
            <small>The actual model identifier used in API calls</small>
          </div>

          <div className="aim-form-group">
            <label>Description</label>
            <textarea value={formData.description} onChange={(e) => handleChange("description", e.target.value)} placeholder="Optional description" rows={2} />
          </div>
        </div>

        <div className="aim-form-section">
          <h3>Configuration</h3>

          <div className="aim-form-row">
            <div className="aim-form-group">
              <label>Max Tokens</label>
              <input type="number" value={formData.maxTokens} onChange={(e) => handleChange("maxTokens", parseInt(e.target.value) || 2048)} min={1} max={100000} />
            </div>

            <div className="aim-form-group">
              <label>Temperature</label>
              <input type="number" value={formData.temperature} onChange={(e) => handleChange("temperature", parseFloat(e.target.value) || 0.7)} min={0} max={2} step={0.1} />
            </div>
          </div>

          <div className="aim-form-row checkboxes">
            <label className="aim-checkbox-label">
              <input type="checkbox" checked={formData.isActive} onChange={(e) => handleChange("isActive", e.target.checked)} />
              Active
            </label>

            <label className="aim-checkbox-label">
              <input type="checkbox" checked={formData.isDefault} onChange={(e) => handleChange("isDefault", e.target.checked)} />
              Set as Default for Provider
            </label>
          </div>
        </div>

        <div className="aim-form-actions">
          <WebButton type="button" text="Cancel" onClick={() => navigate("/admin/ai-management")} className="aim-cancel-btn" />
          <WebButton type="submit" text={isEdit ? "Update Model" : "Create Model"} className="aim-submit-btn" />
        </div>
      </form>

      {/* AWS Setup Guide */}
      {formData.provider === "bedrock" && !awsStatus.configured && (
        <div id="aws-setup-guide" className="aws-setup-guide">
          <h3><FaInfoCircle /> How to Get AWS Bedrock Access Keys</h3>
          <div className="setup-steps">
            <div className="step">
              <span className="step-number">1</span>
              <div className="step-content">
                <h4>Create an IAM User</h4>
                <p>Go to AWS Console → IAM → Users → Create User</p>
                <p>Name it something like <code>bedrock-api-user</code></p>
              </div>
            </div>
            <div className="step">
              <span className="step-number">2</span>
              <div className="step-content">
                <h4>Attach Bedrock Permissions</h4>
                <p>Attach the policy: <code>AmazonBedrockFullAccess</code></p>
                <p>Or create a custom policy with these permissions:</p>
                <pre>{`{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Action": [
      "bedrock:InvokeModel",
      "bedrock:ListFoundationModels",
      "bedrock:GetFoundationModel"
    ],
    "Resource": "*"
  }]
}`}</pre>
              </div>
            </div>
            <div className="step">
              <span className="step-number">3</span>
              <div className="step-content">
                <h4>Create Access Keys</h4>
                <p>Go to the user → Security credentials → Create access key</p>
                <p>Select "Application running outside AWS"</p>
                <p>Download or copy the Access Key ID and Secret Access Key</p>
              </div>
            </div>
            <div className="step">
              <span className="step-number">4</span>
              <div className="step-content">
                <h4>Enable Bedrock Models</h4>
                <p>Go to AWS Console → Amazon Bedrock → Model access</p>
                <p>Request access to the models you want to use (Nova, Claude, etc.)</p>
                <p>Some models require approval which may take a few minutes</p>
              </div>
            </div>
            <div className="step">
              <span className="step-number">5</span>
              <div className="step-content">
                <h4>Add to Environment</h4>
                <p>Add these to your backend <code>.env</code> file:</p>
                <pre>{`AWS_BEDROCK_ACCESS_KEY_ID=your_access_key_id
AWS_BEDROCK_SECRET_ACCESS_KEY=your_secret_access_key
AWS_BEDROCK_REGION=us-east-1`}</pre>
                <p>Then restart your backend server.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
